//src/theme/AppThemeProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

export type AppTheme = 'light' | 'dark';
const THEME_KEY = 'appTheme';

type Ctx = { mode: AppTheme; setMode: (m: AppTheme) => void };
const ThemeCtx = createContext<Ctx | null>(null);

export function useAppTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useAppTheme must be used inside AppThemeProvider');
  return ctx;
}

const initialMode = (): AppTheme => {
  const saved = localStorage.getItem(THEME_KEY) as AppTheme | null;
  if (saved === 'dark' || saved === 'light') return saved;
  return 'light';
};

const AppThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<AppTheme>(initialMode);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, mode);
    // для своих стилей (CSS-хуки)
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const algo = mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;
  const ctx = useMemo(() => ({ mode, setMode }), [mode]);

  return (
    <ThemeCtx.Provider value={ctx}>
      <ConfigProvider theme={{ algorithm: algo }}>
        {children}
      </ConfigProvider>
    </ThemeCtx.Provider>
  );
};

export default AppThemeProvider;
